<template>
  <div class="tw:mt-8">
    <div class="tw:flex tw:lg:items-center tw:justify-between tw:flex-col tw:lg:flex-row">
      <SectionHeader :title="hasRateConnect ? $t('trivago_rc_performance_header') : $t('performance_trivago_header')" />

      <SecondaryButton
        v-if="hasRateConnect"
        size="medium"
        class="tw:mt-4 tw:lg:mt-0 tw:w-auto tw:self-start tw:flex tw:items-center"
        @click="redirectToCampaign"
      >
        <span>{{ $t('tbs_home_manage_campaign_cta') }}</span>
        <ChevronRight class="tw:size-6" />
      </SecondaryButton>
    </div>
    <div class="tw:mb-8">
      <PerformanceNoData v-if="performanceState === 'NO_DATA'" />
      <PerformanceData
        v-if="performanceState === 'ELIGIBLE_NO_CAMPAIGN' || performanceState === 'NOT_ELIGIBLE'"
        :periodSummary="allStatsData"
        :statsData="statsData"
        :hasStatistics="hasStatistics"
        :isRateConnectEligible="performanceState === 'ELIGIBLE_NO_CAMPAIGN'"
        :visits="visits"
        :partnerDistribution="partnerDistribution"
      />
      <RateConnectPerformanceData
        v-if="performanceState === 'ACTIVE_CAMPAIGN'"
        :periodSummary="allStatsData"
        :hasStatistics="hasStatistics"
        :statsData="statsData"
        :isInitialLoading="isInitialLoading"
        :hasRateConnect="true"
        :partnerDistribution="partnerDistribution"
      />
      <Teleport to="body">
        <EligibilityPopup
          :isOpen="isPopupOpen"
          :text="popupConfig.text"
          :buttonLabel="popupConfig.buttonLabel"
          @closePopup="closePopup"
          @onAction="handleAction"
        />
      </Teleport>
    </div>
  </div>
</template>
<script setup>
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'

import SectionHeader from '@/components/homePage/SectionHeader.vue'
import PerformanceData from '@/components/homePage/performance/PerformanceData.vue'
import RateConnectPerformanceData from '@/components/homePage/performance/RateConnectPerformanceData.vue'
import SecondaryButton from '@/components/base/buttons/SecondaryButton.vue'
import ChevronRight from '@/components/icons/ChevronRight.svg?component'
import PerformanceNoData from '@/components/homePage/performance/PerformanceNoData.vue'
import EligibilityPopup from '@/components/homePage/performance/EligibilityPopup.vue'

import { useConversionFunnelData, useConversionFunnelDataDefault } from '@/components/homePage/queries/conversionFunnel'
import { selectedAccommodationId } from '@/layouts/queries'
import { useStats } from '@/components/rateConnect/queries'
import { useRateConnectPerformance } from '@/components/rateConnect/performance/useRateConnectPerformance'
import { useSessionValues } from '@/composables/useSessionValues'
import { useEligibilityPopup } from '@/components/homePage/performance/useEligibilityPopup'

import appAnalytics from '@/utils/tracking'
import useConversionsData from '@/components/homePage/useConversionsData'

const props = defineProps({
  hasRateConnect: Boolean,
  isRateConnectEligible: Boolean,
})

const router = useRouter()
const { isPopupOpen, popupConfig, closePopup, handleAction } = useEligibilityPopup()
const { selectedMarket, formattedStartDate, formattedEndDate, selectedAggregation } = useRateConnectPerformance()
const { defaultStartDate, defaultEndDate, startDate, endDate } = useConversionsData()
const { currency } = useSessionValues()
const { periodSummary, statsData, hasStatistics } = useStats(
  currency,
  formattedStartDate,
  formattedEndDate,
  selectedMarket,
  selectedAggregation
)
const { hasNoInitialData } = useConversionFunnelDataDefault(defaultStartDate, defaultEndDate, ref('daily'))
const { views, visits, viewsVariation, visitsVariation, partnerDistribution } = useConversionFunnelData(
  startDate,
  endDate,
  'daily'
)

const performanceState = computed(() => {
  if (!props.hasRateConnect && hasNoInitialData.value) {
    return 'NO_DATA'
  }
  if (!props.hasRateConnect && props.isRateConnectEligible) {
    return 'ELIGIBLE_NO_CAMPAIGN'
  }
  if (!props.hasRateConnect && !props.isRateConnectEligible) {
    return 'NOT_ELIGIBLE'
  }
  if (props.hasRateConnect && (statsData?.length > 0 || hasStatistics)) {
    return 'ACTIVE_CAMPAIGN'
  }
  return 'CAMPAIGN_NO_DATA'
})

const redirectToCampaign = () => {
  appAnalytics.track(appAnalytics.events.HOMEPAGE_RC_MANAGE_CAMPAIGN, {
    accommodationId: selectedAccommodationId.value,
  })
  router.push({ name: 'rate-connect-running-performance' })
}

const allStatsData = computed(() => {
  return performanceState.value === 'NOT_ELIGIBLE'
    ? [
        {
          title: 'kpi_chart_views',
          id: 'impressions',
          hint: 'infobubble_definition_views',
          total: views.value,
          value: null,
          variation: viewsVariation.value,
        },
        {
          title: 'kpi_chart_visits',
          id: 'clicks',
          hint: 'infobubble_definition_clicks',
          total: visits.value,
          value: null,
          variation: visitsVariation.value,
        },
      ]
    : [
        {
          title: 'kpi_chart_views',
          id: 'impressions',
          hint: 'infobubble_definition_views',
          total:
            performanceState.value === 'ELIGIBLE_NO_CAMPAIGN' ? views.value : periodSummary.value?.totalImpressions,
          value: performanceState.value === 'ELIGIBLE_NO_CAMPAIGN' ? null : periodSummary.value?.campaignImpressions,
          variation: viewsVariation.value,
        },
        {
          title: 'kpi_chart_visits',
          id: 'clicks',
          hint: 'infobubble_definition_clicks',
          total: performanceState.value === 'ELIGIBLE_NO_CAMPAIGN' ? visits.value : periodSummary.value?.totalClicks,
          value: performanceState.value === 'ELIGIBLE_NO_CAMPAIGN' ? null : periodSummary.value?.campaignClicks,
          variation: visitsVariation.value,
        },
        {
          title: 'rc_bookings',
          id: 'bookings',
          hint: 'rc_def_bookings',
          total: null,
          value: periodSummary.value?.bookings,
          variation: periodSummary.value?.bookingsVariationOverPeriod,
        },
        {
          title: 'rc_revenue',
          id: 'revenue',
          hint: 'rc_def_revenue',
          total: null,
          value: periodSummary.value?.bookingRevenue,
          variation: periodSummary.value?.bookingRevenueVariationOverPeriod,
        },
      ]
})
</script>
